import { inject, Injectable, Injector, ProviderToken } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { RemitRegisterColumnId } from '@ph-core/utils/remit-register-columns';
import { ContractType } from '@ph-model/api';
import { ConfigStore, ConfigStoreType } from '@ph-store/config/config.store';

import { TemplateService } from './template.service';

@Injectable({
  providedIn: 'root',
})
export class LazyTemplateService {
  private injector = inject(Injector);
  private _templateService: TemplateService | undefined;
  private _printInProgress = false;
  private configStore: ConfigStoreType = inject(ConfigStore);
  private translate = inject(TranslateService);

  async printRemit(
    contracts: ContractType[] = [],
    remitted: boolean,
    navbarLogo: string | undefined,
    totalDealerCost: number,
    columnIds?: RemitRegisterColumnId[]
  ): Promise<void> {
    if (!navbarLogo) {
      console.error('TemplateService: printRemit: Navbar logo is required - aborting print');
      return;
    }
    if (this._printInProgress) {
      return;
    }

    this._printInProgress = true;
    let previewWindow: Window | null = null;
    try {
      previewWindow = this._openPrintPreview();
      this.configStore.setTemplateServiceLoading();
      const service = await this._initTemplateService();
      await service.printRemit(contracts, remitted, navbarLogo, totalDealerCost, columnIds, previewWindow);
    } catch (error) {
      this._closePreview(previewWindow);
      console.error('TemplateService: printRemit failed', error);
    } finally {
      this._printInProgress = false;
      this.configStore.resetTemplateServiceLoading();
    }
  }

  async printQuote(navbarLogo: string | undefined): Promise<void> {
    if (!navbarLogo) {
      console.error('TemplateService: printQuote: Navbar logo is required - aborting print');
      return;
    }
    if (this._printInProgress) {
      return;
    }

    this._printInProgress = true;
    this.configStore.setTemplateServiceLoading();
    try {
      const service = await this._initTemplateService();
      await service.printQuote(navbarLogo);
    } catch (error) {
      console.error('TemplateService: printQuote failed', error);
    } finally {
      this._printInProgress = false;
      this.configStore.resetTemplateServiceLoading();
    }
  }

  private async _initTemplateService(): Promise<TemplateService> {
    if (!this._templateService) {
      this._templateService = await this.get<TemplateService>(() =>
        import('./template.service').then((m) => m.TemplateService)
      );
    }

    return this._templateService;
  }

  private _openPrintPreview(): Window | null {
    const preview = window.open('', '_blank');
    if (!preview) {
      return null;
    }

    // Keep a handle for the print preview, but drop the reverse link so the new window cannot navigate this one.
    preview.opener = null;

    try {
      const message = this.translate.instant('common.loading');
      const body = preview.document.body;
      preview.document.title = message;
      if (body) {
        body.textContent = message;
        body.style.margin = '0';
        body.style.padding = '24px';
        body.style.fontFamily = 'sans-serif';
      }
    } catch (error) {
      console.error('TemplateService: failed to prepare print preview', error);
    }

    return preview;
  }

  private _closePreview(preview: Window | null): void {
    if (preview && !preview.closed) {
      preview.close();
    }
  }

  private async get<T>(providerLoader: () => Promise<ProviderToken<T>>) {
    return this.injector.get(await providerLoader());
  }
}
